import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Mic, Volume2, VolumeX, Phone, PhoneOff, MessageCircle, Lock } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Lottie from 'lottie-react';

const VoiceAssistant = () => {
  const navigate = useNavigate();
  
  // Check if user has completed enquiry form
  const [hasEnquiryAccess, setHasEnquiryAccess] = useState(false);
  const [selectedVoice, setSelectedVoice] = useState('voice1');
  
  // Lottie animation data state
  const [lottieAnimationData, setLottieAnimationData] = useState(null);

  // Connection & Session State
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [connectionError, setConnectionError] = useState(null);

  // Audio States
  const [isRecording, setIsRecording] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0); // Audio intensity level (0-100)

  // UI States
  const [conversationHistory, setConversationHistory] = useState([]);
  
  // Connection settings - using ngrok for real-world testing
  const serverUrl = process.env.REACT_APP_VOICE_ASSISTANT_WS_URL || 'wss://quality-dog-intent.ngrok-free.app/ws/browser_audio/';
  const apiKey = process.env.REACT_APP_VOICE_ASSISTANT_API_KEY || 'mysecretapikey123';

  // Refs
  const wsRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const streamRef = useRef(null);
  const animationFrameRef = useRef(null);
  const isSpeakingRef = useRef(false); // Ref to track speaking state for audio processor
  const processorRef = useRef(null); // Store processor reference for cleanup
  const audioQueueRef = useRef([]); // Queue for sequential audio playback
  const currentAudioSourceRef = useRef(null); // Track current playing audio
  const isPlayingAudioRef = useRef(false); // Track if audio is currently playing

  // Audio configuration
  const SAMPLE_RATE = 16000;

  // Voice options configuration
  const voiceOptions = [
    {
      id: 'voice1',
      name: 'AI Assistant Voice 1',
      description: 'Professional and clear',
      unlocked: true,
      icon: '😊' // Friendly and approachable
    },
    {
      id: 'voice2', 
      name: 'AI Assistant Voice 2',
      description: 'Warm and friendly',
      unlocked: false,
      icon: '🎵' // Musical note - more visible
    },
    {
      id: 'voice3',
      name: 'AI Assistant Voice 3', 
      description: 'Dynamic and energetic',
      unlocked: false,
      icon: '⚡' // Energetic and dynamic
    },
    {
      id: 'voice4',
      name: 'AI Assistant Voice 4',
      description: 'Calm and soothing',
      unlocked: false,
      icon: '🌸' // Calm and peaceful
    }
  ];

  // Logging function
  const addLog = useCallback((message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    //console.log(`[${timestamp}] ${type.toUpperCase()}: ${message}`);
  }, []);

  // Audio queue management for sequential playback
  const playNextAudioChunk = useCallback(() => {
    if (isPlayingAudioRef.current || audioQueueRef.current.length === 0) {
      return;
    }

    const audioBuffer = audioQueueRef.current.shift();
    isPlayingAudioRef.current = true;

    try {
      const source = audioContextRef.current.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(audioContextRef.current.destination);
      currentAudioSourceRef.current = source;

      source.onended = () => {
        //console.log('[Audio Queue] Audio chunk finished, playing next...');
        isPlayingAudioRef.current = false;
        currentAudioSourceRef.current = null;
        
        // Play next chunk in queue
        setTimeout(() => playNextAudioChunk(), 10);
        
        // If no more chunks and TTS is complete, clear speaking state
        if (audioQueueRef.current.length === 0) {
          //console.log('[Audio Queue] All audio chunks played');
        }
      };

      source.start();
      //console.log('[Audio Queue] Playing audio chunk, queue length:', audioQueueRef.current.length);
      
    } catch (error) {
      console.error('[Audio Queue] Error playing audio chunk:', error);
      isPlayingAudioRef.current = false;
      currentAudioSourceRef.current = null;
      // Try next chunk on error
      setTimeout(() => playNextAudioChunk(), 10);
    }
  }, []);

  // Stop all audio playback and clear queue
  const stopAllAudio = useCallback(() => {
    //console.log('[Audio Queue] Stopping all audio and clearing queue');
    
    // Stop current playing audio
    if (currentAudioSourceRef.current) {
      try {
        currentAudioSourceRef.current.onended = null;
        currentAudioSourceRef.current.stop();
      } catch (e) {
        //console.log('[Audio Queue] Audio already stopped');
      }
      currentAudioSourceRef.current = null;
    }
    
    // Clear queue
    audioQueueRef.current = [];
    isPlayingAudioRef.current = false;
    
    //console.log('[Audio Queue] Audio stopped and queue cleared');
  }, []);

  // Initialize audio context and analyzer
  const initializeAudio = useCallback(async () => {
    try {
      addLog('Initializing audio context...', 'info');
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)({
          sampleRate: SAMPLE_RATE
        });
      }

      if (audioContextRef.current.state === 'suspended') {
        await audioContextRef.current.resume();
      }

      addLog('Requesting microphone access...', 'info');
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: SAMPLE_RATE,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });

      streamRef.current = stream;
      addLog('Microphone access granted', 'success');

  // Set up audio analysis for visualization
      const source = audioContextRef.current.createMediaStreamSource(stream);
      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = 256;
      source.connect(analyserRef.current);

      // Start audio level monitoring for voice-responsive effects
      const monitorAudioLevel = () => {
        if (analyserRef.current && isRecording) {
          const bufferLength = analyserRef.current.frequencyBinCount;
          const dataArray = new Uint8Array(bufferLength);
          analyserRef.current.getByteFrequencyData(dataArray);
          
          // Calculate average volume level
          const average = dataArray.reduce((sum, value) => sum + value, 0) / bufferLength;
          const normalizedLevel = Math.min(100, (average / 255) * 100);
          
          // Only update if recording and not during AI speech
          if (isRecording && !isSpeakingRef.current) {
            setAudioLevel(normalizedLevel);
            //console.log('🎤 Audio Level:', normalizedLevel, 'isRecording:', isRecording, 'isSpeaking:', isSpeakingRef.current);
          }
        }
        
        animationFrameRef.current = requestAnimationFrame(monitorAudioLevel);
      };
      
      // Start monitoring
      monitorAudioLevel();

      addLog('Setting up audio processing for PCM streaming...', 'info');
      
      // Create ScriptProcessorNode for direct PCM audio capture
      const processor = audioContextRef.current.createScriptProcessor(4096, 1, 1);
      source.connect(processor);
      processor.connect(audioContextRef.current.destination);
      
      processor.onaudioprocess = (event) => {
        // CRITICAL: Only send audio if AI is NOT speaking to prevent feedback loop
        if (wsRef.current?.readyState === WebSocket.OPEN && 
            mediaRecorderRef.current?.isRecording && 
            !isSpeakingRef.current) {
          const inputBuffer = event.inputBuffer;
          const inputData = inputBuffer.getChannelData(0);
          
          // Convert Float32 to Int16 PCM
          const int16Array = new Int16Array(inputData.length);
          for (let i = 0; i < inputData.length; i++) {
            int16Array[i] = Math.max(-32768, Math.min(32767, Math.floor(inputData[i] * 32768)));
          }
          
          // Send PCM data directly
          wsRef.current.send(int16Array.buffer);
        } else {
          // Log why audio is being blocked for debugging
          if (isSpeakingRef.current) {
            //console.log('[Audio Processor] BLOCKED: AI is speaking (isSpeakingRef.current =', isSpeakingRef.current, ')');
          } else if (!mediaRecorderRef.current?.isRecording) {
            //console.log('[Audio Processor] BLOCKED: Not recording');
          } else if (wsRef.current?.readyState !== WebSocket.OPEN) {
            //console.log('[Audio Processor] BLOCKED: WebSocket not open');
          }
        }
      };
      
      // Store processor reference for cleanup and access
      processorRef.current = processor;
      mediaRecorderRef.current = { processor, isRecording: false };
      addLog('Audio processing setup completed', 'success');

      addLog('Audio initialization completed successfully', 'success');
      return true;
    } catch (error) {
      addLog(`Error initializing audio: ${error.message}`, 'error');
      setConnectionError(`Microphone access denied: ${error.message}`);
      return false;
    }
  }, [addLog]);

  // WebSocket connection management
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const connectWebSocket = useCallback(async () => {
    if (isConnecting || isConnected) return;

    addLog('Starting connection process...', 'info');
    setIsConnecting(true);
    setConnectionError(null);

    try {
      // Initialize audio first
      const audioInitialized = await initializeAudio();
      if (!audioInitialized) {
        setIsConnecting(false);
        return;
      }

      // Connect WebSocket with API key
      addLog('Connecting to WebSocket...', 'info');
      const wsUrl = `${serverUrl}?api_key=${encodeURIComponent(apiKey)}`;
      wsRef.current = new WebSocket(wsUrl);

      wsRef.current.onopen = () => {
        addLog('WebSocket connected', 'success');
        setIsConnected(true);
        setIsConnecting(false);
        setConnectionError(null);
        
        // Send initial config
        wsRef.current.send(JSON.stringify({
          type: 'config',
          sampleRate: SAMPLE_RATE
        }));

        // Start recording after connection is established
        setTimeout(() => {
          startRecording();
        }, 100); // Small delay to ensure everything is ready
      };

      wsRef.current.onmessage = (event) => {
        if (typeof event.data === 'string') {
          // Handle JSON control messages
          try {
            const data = JSON.parse(event.data);
            //console.log('[WebSocket] Received control message:', data);
            handleControlMessage(data);
          } catch (err) {
            console.error('Error parsing WebSocket message:', err);
          }
        } else {
          // Handle binary audio data (WAV format)
          //console.log('[WebSocket] Received binary audio data, length:', event.data.byteLength);
          handleAudioResponse(event.data);
        }
      };

      wsRef.current.onerror = (error) => {
        addLog(`WebSocket error: ${error.message || 'Connection failed'}`, 'error');
        setConnectionError('Failed to connect to voice assistant server');
        setIsConnecting(false);
      };

      wsRef.current.onclose = () => {
        addLog('WebSocket disconnected', 'warning');
        setIsConnected(false);
        setIsConnecting(false);
        stopRecording();
        cleanup();
      };

    } catch (error) {
      console.error('Connection error:', error);
      setConnectionError(`Connection failed: ${error.message}`);
      setIsConnecting(false);
    }
  }, [isConnecting, isConnected, serverUrl, apiKey, initializeAudio, addLog]);

  // Handle control messages from server
  const handleControlMessage = useCallback((data) => {
    switch (data.type) {
      case 'session_id':
        setSessionId(data.session_id);
        addLog(`Session started: ${data.session_id}`, 'success');
        break;
      
      case 'control':
        switch (data.action) {
          case 'PREPARE_TTS':
            //console.log('[Control] PREPARE_TTS - Blocking audio input immediately');
            setIsSpeaking(true);
            isSpeakingRef.current = true;
            // Stop any currently playing audio and clear queue
            stopAllAudio();
            // Stop any queued audio processing immediately
            if (mediaRecorderRef.current?.processor) {
              //console.log('[Control] Audio processor blocked for TTS');
            }
            break;
          case 'STOP_TTS':
          case 'TTS_COMPLETE':
            //console.log('[Control] TTS_COMPLETE - Re-enabling audio input');
            setIsSpeaking(false);
            isSpeakingRef.current = false;
            // Small delay to ensure TTS audio has finished playing
            setTimeout(() => {
              //console.log('[Control] Audio input fully re-enabled after TTS completion');
            }, 100);
            break;
          case 'AUTO_DISCONNECT_WARNING':
            setConnectionError(data.message);
            break;
          default:
            //console.log('Unknown control action:', data.action);
            break;
        }
        break;
        
      default:
        //console.log('Unknown message type:', data);
        break;
    }
  }, [addLog, stopAllAudio]);

  // Handle audio response from server
  const handleAudioResponse = useCallback(async (audioData) => {
    if (isMuted) return;

    try {
      // Ensure AI speaking state is set immediately when audio starts
      if (!isSpeakingRef.current) {
        //console.log('[Audio Response] Setting AI speaking state to TRUE');
        setIsSpeaking(true);
        isSpeakingRef.current = true;
      }

      // Create audio context if needed
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }

      // Check if audioData is ArrayBuffer
      let buffer;
      if (audioData instanceof ArrayBuffer) {
        buffer = audioData;
      } else if (audioData instanceof Blob) {
        buffer = await audioData.arrayBuffer();
      } else {
        console.error('Unsupported audio data type:', typeof audioData);
        return;
      }

      // Decode audio and add to queue for sequential playback
      const audioBuffer = await audioContextRef.current.decodeAudioData(buffer.slice());
      audioQueueRef.current.push(audioBuffer);
      //console.log('[Audio Queue] Added audio chunk to queue, total chunks:', audioQueueRef.current.length);
      
      // Start playing if not already playing
      if (!isPlayingAudioRef.current) {
        playNextAudioChunk();
      }
      
    } catch (error) {
      console.error('Error processing audio response:', error);
      // Reset speaking state on error
      setIsSpeaking(false);
      isSpeakingRef.current = false;
    }
  }, [isMuted, playNextAudioChunk]);

  // Recording controls
  const startRecording = useCallback(() => {
    addLog(`startRecording called, isRecording: ${isRecording}, audio processor ready: ${!!mediaRecorderRef.current}`, 'debug');
    
    if (!mediaRecorderRef.current || isRecording) {
      addLog('Cannot start recording - Audio processor not ready or already recording', 'error');
      return;
    }

    try {
      addLog('Starting audio recording...', 'debug');
      mediaRecorderRef.current.isRecording = true;
      setIsRecording(true);
      addLog('Recording started successfully', 'success');
    } catch (error) {
      addLog(`Error starting recording: ${error.message}`, 'error');
      setConnectionError(`Recording failed: ${error.message}`);
    }
  }, [isRecording, addLog]);

  const stopRecording = useCallback(() => {
    if (!mediaRecorderRef.current || !isRecording) return;

    try {
      mediaRecorderRef.current.isRecording = false;
      setIsRecording(false);
      addLog('Recording stopped', 'info');
    } catch (error) {
      addLog(`Error stopping recording: ${error.message}`, 'error');
    }
  }, [isRecording, addLog]);

  // Cleanup function
  const cleanup = useCallback(() => {
    // Stop audio processing
    if (processorRef.current) {
      processorRef.current.disconnect();
      processorRef.current = null;
    }
    
    if (mediaRecorderRef.current?.processor) {
      mediaRecorderRef.current.processor.disconnect();
      mediaRecorderRef.current = null;
    }

    // Close media stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    // Cancel animation frame
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    // Reset states
    setIsRecording(false);
    setIsSpeaking(false);
    isSpeakingRef.current = false;
    setSessionId(null);
  }, []);

  // Disconnect and cleanup
  const disconnect = useCallback(() => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      // Send stop signal to immediately interrupt any ongoing TTS
      try {
        wsRef.current.send(JSON.stringify({
          type: 'control',
          action: 'STOP_ALL'
        }));
        console.log('🛑 Sent STOP_ALL signal to backend');
      } catch (error) {
        console.log('Failed to send stop signal:', error);
      }
      
      // Close WebSocket connection
      wsRef.current.close();
    }
    cleanup();
  }, [cleanup]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Cleanup logic directly here to avoid dependency issues
      if (processorRef.current) {
        processorRef.current.disconnect();
      }
      if (mediaRecorderRef.current?.processor) {
        mediaRecorderRef.current.processor.disconnect();
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  // Check if user has completed enquiry form
  useEffect(() => {
    const enquiryCompleted = localStorage.getItem('enquiryCompleted');
    if (enquiryCompleted === 'true') {
      setHasEnquiryAccess(true);
    }
  }, []);

  // Load Lottie animation data
  useEffect(() => {
    const loadLottieAnimation = async () => {
      try {
        // Load your Voice.json Lottie animation file
        const response = await fetch('/lottie/Voice.json');
        
        if (response.ok) {
          const animationData = await response.json();
          setLottieAnimationData(animationData);
          console.log('✅ Voice Lottie animation loaded successfully!');
          console.log('Animation details:', {
            dimensions: `${animationData.w}x${animationData.h}px`,
            duration: `${animationData.op} frames at ${animationData.fr}fps`,
            layers: animationData.layers.length
          });
        } else {
          console.log('❌ Voice.json not found, using fallback circles');
        }
      } catch (error) {
        console.log('❌ Error loading Lottie animation:', error);
        console.log('Will use fallback circle animations instead');
      }
    };

    loadLottieAnimation();
  }, []);

  // Render access denied screen if enquiry not completed
  if (!hasEnquiryAccess) {
    return (
      <section className="min-h-screen bg-gradient-to-br from-gray-50 via-purple-50 to-blue-50 flex items-center justify-center p-4">
        {/* Background animations */}
        <div className="fixed inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-[-10%] left-[-10%] w-64 h-64 md:w-96 md:h-96 bg-blue-200 rounded-full mix-blend-multiply filter blur-3xl animate-blob" />
          <div className="absolute top-[-5%] right-[-5%] w-64 h-64 md:w-96 md:h-96 bg-purple-200 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-2000" />
          <div className="absolute bottom-[-15%] left-[20%] w-64 h-64 md:w-96 md:h-96 bg-purple-300 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-4000" />
        </div>

        <div className="relative z-10 max-w-2xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="bg-white/90 backdrop-blur-sm rounded-3xl p-8 md:p-12 shadow-2xl border border-purple-100"
          >
            <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full flex items-center justify-center">
              <Lock className="w-10 h-10 text-blue-600" />
            </div>
            
            <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
              Access <span className="text-blue-600">Required</span>
            </h1>
            
            <p className="text-lg text-gray-600 mb-8 leading-relaxed">
              To try our Voice Assistant demo, please fill out the enquiry form first. 
              This helps us understand your needs and provide a personalized experience.
            </p>
            
            <div className="space-y-4">
              <button
                onClick={() => navigate('/enquiry')}
                className="w-full md:w-auto px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-xl shadow-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-300 transform hover:scale-105"
              >
                Fill Enquiry Form
              </button>
              
              {/* <div className="flex items-center justify-center">
                <button
                  onClick={() => navigate('/')}
                  className="flex items-center text-gray-600 hover:text-gray-800 transition-colors"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Home
                </button>
              </div> */}
            </div>
            
            <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-xl">
              <p className="text-sm text-blue-700">
                <strong>Why do we need this?</strong> The enquiry form helps us customize the demo experience 
                and ensure you get the most relevant AI solutions for your business needs from TimeLight Technologies.
              </p>
            </div>
          </motion.div>
        </div>

        <style jsx>{`
          @keyframes blob {
            0% {
              transform: translate(0px, 0px) scale(1);
            }
            33% {
              transform: translate(30px, -50px) scale(1.1);
            }
            66% {
              transform: translate(-20px, 20px) scale(0.9);
            }
            100% {
              transform: translate(0px, 0px) scale(1);
            }
          }
          .animate-blob {
            animation: blob 7s infinite;
          }
          .animation-delay-2000 {
            animation-delay: 2s;
          }
          .animation-delay-4000 {
            animation-delay: 4s;
          }
        `}</style>
      </section>
    );
  }

  return (
    <section className="relative min-h-screen bg-gradient-to-b from-white via-purple-50 to-purple-200">
      {/* Background Blobs - Matching Hero component */}
      <div className="absolute inset-0">
        <div className="absolute w-full h-full opacity-30">
          <div className="absolute top-0 -left-4 w-48 md:w-72 h-48 md:h-72 bg-blue-200 rounded-full mix-blend-multiply filter blur-xl animate-blob"></div>
          <div className="absolute top-0 -right-4 w-48 md:w-72 h-48 md:h-72 bg-purple-300 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-2000"></div>
          <div className="absolute -bottom-8 left-20 w-48 md:w-72 h-48 md:h-72 bg-purple-400 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-4000"></div>
          <div className="absolute -top-16 right-1/4 w-64 md:w-96 h-64 md:h-96 bg-indigo-300 rounded-full mix-blend-multiply filter blur-2xl animate-pulse animation-delay-1000"></div>
        </div>
      </div>

      <div className="relative z-10 container mx-auto px-4 py-16 max-w-6xl">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="text-center mb-12"
        >
          {/* <button
            onClick={() => navigate('/')}
            className="inline-flex items-center text-gray-600 hover:text-gray-800 transition-colors mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </button> */}
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-800 mb-4">
            Voice{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-blue-600">
              Assistant
            </span>{" "}
            Demo
          </h1>
          <p className="text-lg md:text-xl text-gray-700 max-w-3xl mx-auto">
            Experience AI-powered voice conversations with real-time responses and natural language understanding
          </p>
        </motion.div>

        {/* Voice Selection Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.8 }}
          className="mb-8"
        >
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-purple-200 shadow-lg">
            <h3 className="text-xl font-semibold mb-6 text-gray-800 text-center">
              Choose Your AI Voice Assistant
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {voiceOptions.map((voice) => (
                <div
                  key={voice.id}
                  className={`relative p-4 rounded-xl border-2 transition-all duration-300 cursor-pointer ${
                    voice.unlocked
                      ? selectedVoice === voice.id
                        ? 'border-purple-500 bg-purple-50 shadow-md'
                        : 'border-gray-200 bg-white hover:border-purple-300 hover:shadow-md'
                      : 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60'
                  }`}
                  onClick={() => {
                    if (voice.unlocked) {
                      setSelectedVoice(voice.id);
                    } else {
                      alert('🔒 This voice is premium! Contact us at ypradeep@ee.iith.ac.in to get access to more voice options and advanced features.');
                    }
                  }}
                >
                  {!voice.unlocked && (
                    <div className="absolute top-2 right-2">
                      <Lock className="w-4 h-4 text-gray-400" />
                    </div>
                  )}
                  
                  <div className="text-center">
                    <div className="text-2xl mb-2">{voice.icon}</div>
                    <h4 className="font-semibold text-gray-800 mb-1 text-sm">{voice.name}</h4>
                    <p className="text-xs text-gray-600">{voice.description}</p>
                    
                    {voice.unlocked && selectedVoice === voice.id && (
                      <div className="mt-2">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                          ✓ Selected
                        </span>
                      </div>
                    )}
                    
                    {!voice.unlocked && (
                      <div className="mt-2">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                          🔒 Premium
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            
            <div className="mt-6 text-center">
              <p className="text-sm text-gray-500">
                Want more voice options? 
                <button 
                  onClick={() => alert('Contact us at ypradeep@ee.iith.ac.in for premium voice packages!')}
                  className="text-purple-600 hover:text-purple-800 underline ml-1"
                >
                  Contact us
                </button> 
                {' '}to unlock premium voices
              </p>
            </div>
          </div>
        </motion.div>

        {/* Control Panel with Connection Status */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.5 }}
          className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 mb-8 border border-purple-200 shadow-lg"
          style={{ minHeight: '600px' }} // Fixed height to prevent layout shift
        >
          {/* Connection Status at the top */}
          <div className="mb-8">
            <h4 className="text-lg font-semibold text-gray-800 mb-4 text-center">Connection Status</h4>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <div className={`w-3 h-3 rounded-full transition-all ${
                  isConnected ? 'bg-green-500 shadow-lg shadow-green-500/30' : 
                  isConnecting ? 'bg-yellow-500 animate-pulse shadow-lg shadow-yellow-500/30' : 
                  'bg-red-500 shadow-lg shadow-red-500/30'
                }`} />
                <span className="text-gray-700 font-medium text-sm">
                  {isConnected ? 'Connected' : isConnecting ? 'Connecting...' : 'Disconnected'}
                </span>
              </div>
              
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <div className={`relative ${isRecording ? 'animate-pulse' : ''}`}>
                  <Mic className={`w-4 h-4 transition-colors ${isRecording ? 'text-green-500' : 'text-gray-400'}`} />
                  {isRecording && (
                    <div className="absolute -inset-1 bg-green-400 rounded-full opacity-20 animate-ping" />
                  )}
                </div>
                <div className="flex-1">
                  <span className="text-gray-700 font-medium text-sm block">
                    {isRecording ? '🎤 Listening...' : 'Microphone Off'}
                  </span>
                  {/* Voice level indicator */}
                  {isRecording && !isSpeaking && (
                    <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1">
                      <div 
                        className="bg-green-500 h-1.5 rounded-full transition-all duration-100"
                        style={{ width: `${Math.min(100, audioLevel)}%` }}
                      />
                    </div>
                  )}
                </div>
              </div>
              
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <div className={`relative ${isSpeaking ? 'animate-pulse' : ''}`}>
                  <Volume2 className={`w-4 h-4 transition-colors ${isSpeaking ? 'text-blue-500' : 'text-gray-400'}`} />
                  {isSpeaking && (
                    <div className="absolute -inset-1 bg-blue-400 rounded-full opacity-20 animate-ping" />
                  )}
                </div>
                <span className="text-gray-700 font-medium text-sm">
                  {isSpeaking ? '🤖 AI Speaking...' : 'Silent'}
                </span>
              </div>
            </div>

            {/* Session ID */}
            {sessionId && (
              <div className="text-xs text-gray-500 bg-gray-100/50 rounded-lg p-2 text-center mb-4">
                Session: {sessionId.substring(0, 8)}...
              </div>
            )}

            {/* Connection Error */}
            {connectionError && (
              <motion.div 
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm"
              >
                {connectionError}
              </motion.div>
            )}
          </div>

          <div className="flex flex-col items-center gap-6">
            {/* Main Control Button Container - Fixed size to prevent layout shift */}
            <div className="relative flex items-center justify-center" style={{ width: '350px', height: '350px' }}>
              {/* Lottie Animation Background - runs when connected */}
              {lottieAnimationData && (isConnected || isConnecting) && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div 
                    className="absolute transition-all duration-700 ease-in-out"
                    style={{
                      width: '300px',
                      height: '300px',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      // Simple color filters based on voice state
                      filter: isSpeaking 
                        ? 'sepia(1) saturate(3) hue-rotate(200deg) brightness(1.1)' // Blue when AI speaks
                        : isRecording && !isSpeaking 
                        ? 'sepia(1) saturate(3) hue-rotate(90deg) brightness(1.1)' // Green when user speaks
                        : 'none', // No filter - keep original colors when idle
                      opacity: isConnecting ? 0.6 : 0.8
                    }}
                  >
                    <Lottie 
                      animationData={lottieAnimationData}
                      loop={true}
                      autoplay={true}
                      style={{ 
                        width: '100%', 
                        height: '100%',
                        // Subtle scaling based on voice activity
                        transform: isRecording && !isSpeaking 
                          ? `scale(${1 + (audioLevel / 1000)})` 
                          : 'scale(1)',
                        transition: 'transform 0.3s ease'
                      }}
                    />
                  </div>
                </div>
              )}
              
              {/* Main Control Button */}
              <div className="relative z-10">
              
              {/* Fallback: Multi-layered filled circles for recording state (Green when user speaks) */}
              {!lottieAnimationData && isRecording && !isSpeaking && (
                <>
                  {/* Outermost circle - lightest green with transparency */}
                  <div 
                    className="absolute rounded-full animate-pulse"
                    style={{
                      inset: `-${Math.max(80, audioLevel * 2.0)}px`,
                      backgroundColor: `rgba(34, 197, 94, ${Math.max(0.05, audioLevel / 100 * 0.15)})`,
                      animationDuration: '3s'
                    }}
                  />
                  
                  {/* Outer circle - light green */}
                  <div 
                    className="absolute rounded-full animate-pulse"
                    style={{
                      inset: `-${Math.max(60, audioLevel * 1.5)}px`,
                      backgroundColor: `rgba(34, 197, 94, ${Math.max(0.1, audioLevel / 100 * 0.25)})`,
                      animationDuration: '2.5s',
                      animationDelay: '0.2s'
                    }}
                  />
                  
                  {/* Middle circle - medium green */}
                  <div 
                    className="absolute rounded-full animate-pulse"
                    style={{
                      inset: `-${Math.max(40, audioLevel * 1.0)}px`,
                      backgroundColor: `rgba(34, 197, 94, ${Math.max(0.15, audioLevel / 100 * 0.35)})`,
                      animationDuration: '2s',
                      animationDelay: '0.4s'
                    }}
                  />
                  
                  {/* Inner circle - darker green */}
                  <div 
                    className="absolute rounded-full animate-pulse"
                    style={{
                      inset: `-${Math.max(20, audioLevel * 0.5)}px`,
                      backgroundColor: `rgba(34, 197, 94, ${Math.max(0.2, audioLevel / 100 * 0.45)})`,
                      animationDuration: '1.5s',
                      animationDelay: '0.6s'
                    }}
                  />
                </>
              )}
              
              {/* Fallback: Multi-layered filled circles for speaking state (Blue when AI speaks) */}
              {!lottieAnimationData && isSpeaking && (
                <>
                  {/* Outermost circle - lightest blue */}
                  <div 
                    className="absolute rounded-full animate-pulse"
                    style={{
                      inset: '-85px',
                      backgroundColor: 'rgba(59, 130, 246, 0.1)',
                      animationDuration: '3s'
                    }}
                  />
                  
                  {/* Outer circle - light blue */}
                  <div 
                    className="absolute rounded-full animate-pulse"
                    style={{
                      inset: '-65px',
                      backgroundColor: 'rgba(59, 130, 246, 0.2)',
                      animationDuration: '2.5s',
                      animationDelay: '0.2s'
                    }}
                  />
                  
                  {/* Middle circle - medium blue */}
                  <div 
                    className="absolute rounded-full animate-pulse"
                    style={{
                      inset: '-45px',
                      backgroundColor: 'rgba(59, 130, 246, 0.3)',
                      animationDuration: '2s',
                      animationDelay: '0.4s'
                    }}
                  />
                  
                  {/* Inner circle - darker blue */}
                  <div 
                    className="absolute rounded-full animate-pulse"
                    style={{
                      inset: '-25px',
                      backgroundColor: 'rgba(59, 130, 246, 0.4)',
                      animationDuration: '1.5s',
                      animationDelay: '0.6s'
                    }}
                  />
                  
                  {/* Innermost circle - darkest blue */}
                  <div 
                    className="absolute rounded-full animate-pulse"
                    style={{
                      inset: '-10px',
                      backgroundColor: 'rgba(59, 130, 246, 0.5)',
                      animationDuration: '1s',
                      animationDelay: '0.8s'
                    }}
                  />
                </>
              )}

              <motion.button
                onClick={isConnected ? disconnect : connectWebSocket}
                disabled={isConnecting}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className={`
                  group relative w-32 h-32 rounded-full flex items-center justify-center text-white text-3xl
                  transition-all duration-700 shadow-2xl overflow-hidden z-10
                  ${isConnected 
                    ? isSpeaking
                      ? 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-red-500 hover:to-red-600' // Blue when AI speaks
                      : isRecording && !isSpeaking
                      ? 'bg-gradient-to-r from-green-500 to-green-600 hover:from-red-500 hover:to-red-600' // Green when user speaks
                      : 'bg-gradient-to-r from-gray-500 to-gray-600 hover:from-red-500 hover:to-red-600' // Gray when idle
                    : 'bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600' // Purple when disconnected
                  }
                  ${isConnecting ? 'animate-pulse' : ''}
                  disabled:opacity-50 disabled:cursor-not-allowed
                `}
                style={{
                  transform: isRecording && !isSpeaking 
                    ? `scale(${1 + (audioLevel / 2000)})` 
                    : 'scale(1)',
                  transition: 'all 0.3s ease'
                }}
              >
                {/* Subtle inner glow effects for voice activity */}
                {isRecording && !isSpeaking && (
                  <div 
                    className="absolute inset-0 rounded-full group-hover:hidden"
                    style={{
                      background: `radial-gradient(circle, rgba(255, 255, 255, ${Math.max(0.1, audioLevel / 100 * 0.3)}) 0%, rgba(255, 255, 255, ${Math.max(0.05, audioLevel / 100 * 0.15)}) 50%, transparent 100%)`,
                      animation: 'pulse 2s ease-in-out infinite'
                    }}
                  />
                )}
                
                {/* Subtle inner glow effects for AI speaking */}
                {isSpeaking && (
                  <div 
                    className="absolute inset-0 rounded-full group-hover:hidden"
                    style={{
                      background: 'radial-gradient(circle, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0.1) 50%, transparent 100%)',
                      animation: 'pulse 2s ease-in-out infinite'
                    }}
                  />
                )}
                
                {/* Red glow effect on hover when connected */}
                {isConnected && (
                  <div 
                    className="absolute inset-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                    style={{
                      background: 'radial-gradient(circle, rgba(255, 100, 100, 0.3) 0%, rgba(255, 100, 100, 0.1) 50%, transparent 100%)',
                      animation: 'pulse 1.5s ease-in-out infinite'
                    }}
                  />
                )}
                
                {/* Button content */}
                <div className="relative z-10">
                  {isConnecting ? (
                    <div className="animate-spin w-10 h-10 border-4 border-white border-t-transparent rounded-full" />
                  ) : isConnected ? (
                    <PhoneOff size={40} />
                  ) : (
                    <Phone size={40} />
                  )}
                </div>
              </motion.button>
              </div> {/* Close Main Control Button */}
            </div> {/* Close Button Container */}

            {/* Control Buttons */}
            <div className="flex gap-4">
              <motion.button
                onClick={() => setIsMuted(!isMuted)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className={`
                  p-3 rounded-full transition-all duration-200 shadow-lg
                  ${isMuted 
                    ? 'bg-red-100 text-red-600 hover:bg-red-200' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }
                `}
                title={isMuted ? 'Unmute' : 'Mute'}
              >
                {isMuted ? <VolumeX size={20} /> : <Volume2 size={20} />}
              </motion.button>

              <motion.button
                onClick={() => setConversationHistory([])}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="p-3 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 transition-all duration-200 shadow-lg"
                title="Clear conversation"
              >
                <MessageCircle size={20} />
              </motion.button>
            </div>

            {/* Status Text */}
            <div className="text-center">
              <p className="text-lg font-medium text-gray-800">
                {isConnecting && 'Connecting to voice assistant...'}
                {isConnected && !isRecording && 'Connected - Ready to talk'}
                {isConnected && isRecording && !isSpeaking && (
                  <span className="text-green-600 animate-pulse">🎤 Listening...</span>
                )}
                {isConnected && isSpeaking && (
                  <span className="text-blue-600 animate-pulse">🔊 AI is responding...</span>
                )}
                {!isConnected && !isConnecting && 'Click to start conversation'}
              </p>
              
              {isConnected && (
                <p className="text-sm text-gray-600 mt-2">
                  {isRecording && !isSpeaking && 'Start speaking naturally. The AI will respond automatically.'}
                  {isSpeaking && 'AI is speaking...'}
                  {!isRecording && !isSpeaking && 'Ready to listen.'}
                </p>
              )}
            </div>
          </div>
        </motion.div>

        {/* Conversation History */}
        <AnimatePresence>
          {conversationHistory.length > 0 && (
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-purple-200 shadow-lg mb-8"
            >
              <h3 className="text-xl font-semibold mb-4 text-gray-800">Conversation</h3>
              <div className="space-y-4 max-h-96 overflow-y-auto">
                {conversationHistory.map((msg, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: msg.role === 'user' ? 20 : -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`
                        max-w-xs lg:max-w-md px-4 py-3 rounded-2xl
                        ${msg.role === 'user' 
                          ? 'bg-gradient-to-r from-purple-500 to-blue-500 text-white' 
                          : 'bg-gray-100 text-gray-800'
                        }
                      `}
                    >
                      <div className="text-xs opacity-70 mb-1">
                        {msg.role === 'user' ? 'You' : 'AI Assistant'}
                      </div>
                      <div className="text-sm">{msg.content}</div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Debug Logs - Hidden */}
        {/* {process.env.NODE_ENV === 'development' && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.5 }}
            className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-purple-200 shadow-lg mb-8"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-gray-800">Debug Logs</h3>
              <motion.button
                onClick={clearLogs}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-4 py-2 bg-red-100 text-red-600 hover:bg-red-200 rounded-lg transition-all duration-200 text-sm font-medium"
              >
                Clear Logs
              </motion.button>
            </div>
            <div className="bg-gray-900 rounded-lg p-4 max-h-64 overflow-y-auto font-mono text-sm">
              {logs.length === 0 ? (
                <div className="text-gray-500">No logs yet...</div>
              ) : (
                logs.map((log, index) => (
                  <div key={index} className={`
                    ${log.type === 'error' ? 'text-red-400' : 
                      log.type === 'warning' ? 'text-yellow-400' : 
                      log.type === 'success' ? 'text-green-400' : 
                      log.type === 'debug' ? 'text-blue-400' : 'text-gray-300'}
                  `}>
                    <span className="text-gray-500">[{log.timestamp}]</span>{' '}
                    <span className="text-gray-400 uppercase">{log.type}:</span>{' '}
                    {log.message}
                  </div>
                ))
              )}
            </div>
          </motion.div>
        )} */}


        {/* Instructions */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.5 }}
          className="bg-white/60 backdrop-blur-sm rounded-2xl p-6 border border-purple-100 shadow-lg"
        >
          <h3 className="text-lg font-semibold mb-4 text-gray-800">How to use the Voice Assistant:</h3>
          <ul className="space-y-3 text-gray-700">
            <li className="flex items-start gap-3">
              <div className="w-2 h-2 rounded-full bg-purple-500 mt-2 flex-shrink-0"></div>
              <span>Click the phone button to connect and start your voice session</span>
            </li>
            <li className="flex items-start gap-3">
              <div className="w-2 h-2 rounded-full bg-purple-500 mt-2 flex-shrink-0"></div>
              <span>Speak naturally - the AI will detect when you're talking and respond automatically</span>
            </li>
            <li className="flex items-start gap-3">
              <div className="w-2 h-2 rounded-full bg-purple-500 mt-2 flex-shrink-0"></div>
              <span>Use the mute button to silence AI responses while keeping the connection active</span>
            </li>
            <li className="flex items-start gap-3">
              <div className="w-2 h-2 rounded-full bg-purple-500 mt-2 flex-shrink-0"></div>
              <span>The session will automatically disconnect after 30 seconds of inactivity</span>
            </li>
            <li className="flex items-start gap-3">
              <div className="w-2 h-2 rounded-full bg-purple-500 mt-2 flex-shrink-0"></div>
              <span>Your conversation history is maintained throughout the session for context</span>
            </li>
          </ul>
          
          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-xl">
            <h4 className="text-sm font-semibold text-blue-800 mb-2">🔒 Secure Connection</h4>
            <p className="text-sm text-blue-700">
              This demo connects securely to our voice AI backend. No configuration needed - just click and start talking!
            </p>
          </div>
        </motion.div>
      </div>

      {/* CSS for animations matching your theme */}
      <style jsx>{`
        @keyframes blob {
          0% {
            transform: translate(0px, 0px) scale(1);
          }
          33% {
            transform: translate(30px, -50px) scale(1.1);
          }
          66% {
            transform: translate(-20px, 20px) scale(0.9);
          }
          100% {
            transform: translate(0px, 0px) scale(1);
          }
        }
        
        @keyframes breathe {
          0%, 100% {
            transform: scale(1);
            opacity: 0.6;
          }
          50% {
            transform: scale(1.15);
            opacity: 0.9;
          }
        }
        
        @keyframes counter-rotate {
          0% {
            transform: rotate(0deg) scale(1);
          }
          50% {
            transform: rotate(-180deg) scale(1.1);
          }
          100% {
            transform: rotate(-360deg) scale(1);
          }
        }
        
        @keyframes flow {
          0% {
            transform: rotate(0deg) translateX(10px) rotate(0deg);
            opacity: 0.2;
          }
          33% {
            transform: rotate(120deg) translateX(15px) rotate(-120deg);
            opacity: 0.4;
          }
          66% {
            transform: rotate(240deg) translateX(12px) rotate(-240deg);
            opacity: 0.3;
          }
          100% {
            transform: rotate(360deg) translateX(10px) rotate(-360deg);
            opacity: 0.2;
          }
        }
        
        @keyframes interlace {
          0% {
            transform: rotate(0deg) scale(1);
            border-radius: 50%;
          }
          25% {
            transform: rotate(90deg) scale(1.05);
            border-radius: 45%;
          }
          50% {
            transform: rotate(180deg) scale(0.95);
            border-radius: 50%;
          }
          75% {
            transform: rotate(270deg) scale(1.05);
            border-radius: 55%;
          }
          100% {
            transform: rotate(360deg) scale(1);
            border-radius: 50%;
          }
        }
        
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
        .animation-delay-1000 {
          animation-delay: 1s;
        }
        .animation-delay-500 {
          animation-delay: 0.5s;
        }
        .animation-delay-300 {
          animation-delay: 0.3s;
        }
        .animation-delay-600 {
          animation-delay: 0.6s;
        }
      `}</style>
    </section>
  );
};

export default VoiceAssistant;
